import { useEffect, useState } from 'react';
import {
  ArrowLeft,ArrowRight,Building2,Car,Eye,EyeOff,LockKeyhole,Mail,MapPin,ShieldCheck,UserRound
} from 'lucide-react';
import { Marketing } from './Marketing';
import { WorkshopFinder } from './WorkshopFinder';
import { PublicWorkshopProfile } from './WorkshopProfile';
import { PublicHeader } from './PublicHeader';
import { AccessPage } from './AccessPage';
import { ImprintPage, PrivacyPage, PublicLegalStrip } from './LegalPages';
import { CustomerMarketingPage, SecurityMarketingPage, WorkshopMarketingPage } from './MarketingPages';
import { BrandingPage, CustomerPortal, OfficeDashboard, WorkshopBoard } from './Workspace';
import { Brand, type AppView } from './components';
import { backendConfigured, supabase } from './lib';
import { signUpCustomer } from './api';

function Splash(){
  const [show,setShow]=useState(true);
  useEffect(()=>{const id=setTimeout(()=>setShow(false),1100);return()=>clearTimeout(id)},[]);
  if(!show)return null;
  return <div className="splash"><div className="splash-orbit"/><Brand inverse/><div className="splash-loader"><i/></div></div>;
}

async function resolveSignedInView():Promise<AppView>{
  if(!supabase)return'customer';
  const {data:user}=await supabase.auth.getUser();
  if(!user.user)return'login';
  try{await supabase.rpc('claim_my_workshop_invites')}catch{}
  const {data:member}=await supabase.from('workshop_members').select('role').eq('user_id',user.user.id).eq('active',true).limit(1).maybeSingle();
  if(member?.role==='mechanic')return'workshop';
  if(member)return'office';
  if(user.user.user_metadata?.account_intent==='workshop')return'branding';
  return'customer';
}

function Login({setView}:{setView:(view:AppView)=>void}){
  const [tab,setTab]=useState<'login'|'customer'|'workshop'>('login');
  const [email,setEmail]=useState('');const [password,setPassword]=useState('');const [name,setName]=useState('');
  const [street,setStreet]=useState('');const [postalCode,setPostalCode]=useState('');const [city,setCity]=useState('');
  const [show,setShow]=useState(false);const [busy,setBusy]=useState(false);const [message,setMessage]=useState('');

  const submit=async(event:React.FormEvent)=>{
    event.preventDefault();setMessage('');
    if(!backendConfigured||!supabase){setMessage('Backend-Verbindung ist noch nicht aktiv. Du kannst unten die Produktdemo öffnen.');return}
    setBusy(true);
    try{
      if(tab==='login'){
        const {error}=await supabase.auth.signInWithPassword({email,password});
        if(error)throw error;
        setView(await resolveSignedInView());
      }else{
        if(tab==='customer'&&(!street.trim()||!postalCode.trim()||!city.trim()))throw new Error('Bitte die vollständige Anschrift eintragen.');
        const result=await signUpCustomer(email,password,name,{
          accountIntent:tab==='workshop'?'workshop':'customer',
          street:tab==='customer'?street:undefined,
          postalCode:tab==='customer'?postalCode:undefined,
          city:tab==='customer'?city:undefined
        });
        if(result.session){
          setMessage(tab==='workshop'?'Konto erstellt. Jetzt richten wir deine Werkstatt ein.':'Konto erstellt.');
          setView(tab==='workshop'?'branding':'customer');
        }else{
          setMessage('Konto erstellt. Bitte bestätige zuerst deine E-Mail-Adresse. Danach führt MotorAtlas dich automatisch in den richtigen Bereich.');
        }
      }
    }catch(err){setMessage(err instanceof Error?err.message:'Anmeldung nicht möglich.')}
    finally{setBusy(false)}
  };

  return <div className="auth-page">
    <div className="auth-ambient one"/><div className="auth-ambient two"/>
    <button className="back" onClick={()=>setView('home')}><ArrowLeft/> Zurück zu motoratlas.de</button>
    <div className="auth-shell">
      <section className="auth-story">
        <Brand inverse/>
        <div><span className="auth-kicker">MOTORATLAS ACCESS</span><h1>Eine Anmeldung. <em>Die passende Arbeitswelt.</em></h1><p>Autofahrer sehen ihre Garage. Das Büro sieht den Betrieb. Mechaniker sehen nur ihre Werkstattkarten.</p></div>
        <div className="auth-proof"><span><ShieldCheck/> Rollenbasierter Zugriff</span><span><LockKeyhole/> Geschützte Fahrzeugdaten</span><span><Car/> Ausschließlich PKW</span></div>
      </section>
      <section className="auth-card">
        <div className="auth-tabs"><button className={tab==='login'?'active':''} onClick={()=>setTab('login')}>Anmelden</button><button className={tab==='customer'?'active':''} onClick={()=>setTab('customer')}>Autofahrer</button><button className={tab==='workshop'?'active':''} onClick={()=>setTab('workshop')}>Werkstatt</button></div>
        <div className="auth-title"><span>{tab==='login'?'WILLKOMMEN ZURÜCK':tab==='customer'?'DEINE GARAGE':'DEINE WERKSTATT'}</span><h2>{tab==='login'?'Bei MotorAtlas anmelden':tab==='customer'?'Kundenkonto erstellen':'Werkstattkonto starten'}</h2><p>{tab==='login'?'MotorAtlas erkennt nach der Anmeldung automatisch deine Rolle.':tab==='customer'?'Fahrzeuge, Anfragen, Freigaben und Dokumente in einem Konto.':'Nach der Kontoerstellung richtest du Werkstattprofil, Logo und Mitarbeiter ein.'}</p></div>
        <form onSubmit={submit}>
          {tab!=='login'&&<label><span>Name</span><div><UserRound/><input value={name} onChange={e=>setName(e.target.value)} required placeholder={tab==='workshop'?'Ansprechpartner':'Vor- und Nachname'}/></div></label>}
          {tab==='customer'&&<div className="auth-address"><label><span>Straße & Hausnummer</span><div><MapPin/><input value={street} onChange={e=>setStreet(e.target.value)} required placeholder="Musterstraße 12"/></div></label><div><label><span>PLZ</span><input value={postalCode} onChange={e=>setPostalCode(e.target.value.replace(/\D/g,'').slice(0,5))} required inputMode="numeric" placeholder="92421"/></label><label><span>Ort</span><input value={city} onChange={e=>setCity(e.target.value)} required placeholder="Schwandorf"/></label></div></div>}
          <label><span>E-Mail</span><div><Mail/><input type="email" value={email} onChange={e=>setEmail(e.target.value)} required placeholder="name@beispiel.de"/></div></label>
          <label><span>Passwort</span><div><LockKeyhole/><input type={show?'text':'password'} value={password} onChange={e=>setPassword(e.target.value)} minLength={8} required placeholder="••••••••••••"/><button type="button" className="show-password" onClick={()=>setShow(!show)}>{show?<EyeOff/>:<Eye/>}</button></div></label>
          {message&&<div className="auth-message">{message}</div>}
          <button className="btn primary xl full" disabled={busy}>{busy?'Bitte einen Moment …':tab==='login'?'Sicher anmelden':'Konto erstellen'} <ArrowRight/></button>
        </form>
        <div className="demo-divider"><span>Produkt vorab ansehen</span></div>
        <div className="demo-buttons"><button onClick={()=>setView('office')}><Building2/> Büro</button><button onClick={()=>setView('workshop')}><Car/> Werkstatt</button><button onClick={()=>setView('customer')}><UserRound/> Kunde</button></div>
      </section>
    </div>
  </div>;
}

const viewHashes:Record<AppView,string>={
  home:'#/',
  'customer-info':'#/autofahrer',
  'workshop-info':'#/werkstaetten',
  'security-info':'#/sicherheit',
  finder:'#/werkstatt-finden',
  'workshop-profile':'#/werkstattprofil',
  login:'#/anmelden',
  privacy:'#/datenschutz',
  imprint:'#/impressum',
  office:'#/app/buero',
  workshop:'#/app/werkstatt',
  customer:'#/app/kunde',
  branding:'#/app/werkstattprofil'
};

function viewFromHash(hash:string):AppView|null{
  if(hash.startsWith('#/fahrzeug-uebernehmen'))return'login';
  if(hash.startsWith('#/werkstatt/')){
    sessionStorage.setItem('motoratlas_selected_workshop_slug',decodeURIComponent(hash.slice('#/werkstatt/'.length)));
    return 'workshop-profile';
  }
  const entry=(Object.entries(viewHashes) as Array<[AppView,string]>).find(([,value])=>value===hash);
  return entry?.[0]??null;
}

function appBasePath(){
  const path=new URL(import.meta.env.BASE_URL,location.origin).pathname;
  return path.endsWith('/')?path:path+'/';
}

function authPath(segment:string){
  return appBasePath()+segment.replace(/^\/+|\/+$/g,'');
}

function viewFromLocation():AppView|null{
  const path=location.pathname.replace(/\/+$/,'');
  if(path.endsWith('/anmelden')||path.endsWith('/bestaetigung')||path.endsWith('/passwort-zuruecksetzen')||path.endsWith('/fahrzeug-uebernehmen'))return'login';
  return viewFromHash(location.hash);
}

function normalizeSignupCallback(){
  const raw=location.hash.startsWith('#')?location.hash.slice(1):'';
  if(!raw)return;
  const params=new URLSearchParams(raw);
  if(params.get('type')!=='signup')return;
  sessionStorage.setItem('motoratlas_signup_confirmed','1');
  history.replaceState({},'',authPath('bestaetigung'));
}

export default function App(){
  normalizeSignupCallback();
  const [view,setView]=useState<AppView>(()=>viewFromLocation()??'home');

  const navigate=(next:AppView)=>{
    setView(next);
    const profileSlug=next==='workshop-profile'?sessionStorage.getItem('motoratlas_selected_workshop_slug'):null;
    let target:string;
    if(next==='login'){
      target=authPath('anmelden');
    }else{
      const hash=next==='workshop-profile'&&profileSlug?'#/werkstatt/'+encodeURIComponent(profileSlug):viewHashes[next];
      target=appBasePath()+hash;
    }
    if(location.pathname+location.search+location.hash!==target)history.pushState({motorAtlasView:next},'',target);
    window.scrollTo({top:0,behavior:'auto'});
  };

  useEffect(()=>{
    const syncFromLocation=()=>{
      const next=viewFromLocation();
      if(next)setView(next);
    };
    window.addEventListener('popstate',syncFromLocation);
    window.addEventListener('hashchange',syncFromLocation);
    return()=>{
      window.removeEventListener('popstate',syncFromLocation);
      window.removeEventListener('hashchange',syncFromLocation);
    };
  },[]);

  useEffect(()=>{
    if(!supabase)return;
    supabase.auth.getSession().then(async({data})=>{
      if(data.session&&(location.hash==='#app'||new URLSearchParams(location.search).get('app')==='1')){
        navigate(await resolveSignedInView());
      }
    });
  },[]);

  useEffect(()=>{
    const titles:Partial<Record<AppView,string>>={
      home:'MotorAtlas – Werkstatt. Neu gedacht.',
      'customer-info':'MotorAtlas für Autofahrer – Fahrzeug, Werkstatt & Dokumente',
      'workshop-info':'MotorAtlas für Werkstätten – Digitaler Werkstattablauf',
      'security-info':'MotorAtlas – Sicherheit & Transparenz',
      finder:'MotorAtlas – Werkstatt finden',
      'workshop-profile':'MotorAtlas – Werkstattprofil',
      login:location.pathname.replace(/\/+$/,'').endsWith('/bestaetigung')
        ?'MotorAtlas – E-Mail bestätigt'
        :location.pathname.replace(/\/+$/,'').endsWith('/passwort-zuruecksetzen')
          ?'MotorAtlas – Passwort zurücksetzen'
          :'MotorAtlas – Anmelden',
      privacy:'MotorAtlas – Datenschutz',
      imprint:'MotorAtlas – Impressum'
    };
    if(titles[view])document.title=titles[view]!;
  },[view]);

  const showPublicHeader=['home','customer-info','workshop-info','security-info','finder','workshop-profile','login','privacy','imprint'].includes(view);
  const showLegalStrip=['finder','workshop-profile','login'].includes(view);
  return <div className="site">
    <Splash/>
    {showPublicHeader&&<PublicHeader view={view} setView={navigate}/>} 
    {view==='home'&&<Marketing setView={navigate}/>}
    {view==='customer-info'&&<CustomerMarketingPage setView={navigate}/>}
    {view==='workshop-info'&&<WorkshopMarketingPage setView={navigate}/>}
    {view==='security-info'&&<SecurityMarketingPage setView={navigate}/>}
    {view==='finder'&&<WorkshopFinder setView={navigate}/>}
    {view==='workshop-profile'&&<PublicWorkshopProfile setView={navigate}/>}
    {view==='login'&&<AccessPage setView={navigate}/>}
    {view==='office'&&<OfficeDashboard setView={navigate}/>}
    {view==='workshop'&&<WorkshopBoard setView={navigate}/>}
    {view==='customer'&&<CustomerPortal setView={navigate}/>}
    {view==='branding'&&<BrandingPage setView={navigate}/>}
    {showLegalStrip&&<PublicLegalStrip setView={navigate}/>}
  </div>;
}
